import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { createStore, withProps, setProp, select } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  addEntities,
  updateEntities,
  deleteEntities,
  getEntity,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import { IBubbleShooterGame } from './bubble-shooter-game.interface';
import {
  IListParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'bubble-shooter-games';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IBubbleShooterGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class BubbleShooterGameCoreService {
  private _loading = false;

  /**
   * @name bubbleShooterGameList$
   * @description emits stored list of all Bubble shooter games
   */

  bubbleShooterGamesList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  public getBubbleShooterGames(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`bubble-shooter-game/list`, params || {})
      .subscribe((data: IBubbleShooterGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addBubbleShooterGame(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('bubble-shooter-game', dataInput || {}).pipe(
      tap((data: IBubbleShooterGame) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateBubbleShooterGame(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('bubble-shooter-game', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IBubbleShooterGame) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteBubbleShooterGame(dataInput): Observable<any> {
    this._loading = true;
    return this._api.delete('bubble-shooter-game', { body: dataInput }).pipe(
      tap(() => {
        store.update(deleteEntities(dataInput['_id']));
        this._loading = false;
      }),
    );
  }

  public getBubbleShooterGameById(
    bubbleShooterGameId: string,
  ): Observable<IBubbleShooterGame> {
    return this._api.get(`bubble-shooter-game/${bubbleShooterGameId}`);
  }

  public getBubbleShooterGameInfo(id: string): IBubbleShooterGame {
    return store.query(getEntity(id));
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getBubbleShooterGames();
    }
  }
}

interface IBubbleShooterGameListResponse {
  list: IBubbleShooterGame[];
  totalCount: number;
}
